import { Capacitor } from '@capacitor/core';
import type { AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth.store';
import type {
  CircleHistoryEntry,
  CircleJoinData,
  CircleJournal,
  CirclePhase,
  CirclePostSession,
  CircleRoomState,
  CircleSummary,
  EkmanEmotion,
  ListeningRequest,
  MoodCheckInEntry,
  NextCircleInfo,
  PublicCohortData,
  ReactionSummaryEntry,
  ReactionType,
} from '../types';
import type { AvailabilityNotice, IncomingCall, MissedCall, PendingRing } from '../types/ring';
import type {
  PublicStartingSoonResponse,
  StartingNoticeEvent,
  StartingSoon,
} from '../types/startingSoon';
import api from './api';

export const getNextCircle = async (): Promise<NextCircleInfo> => {
  const response = await api.get('/circles/next');
  return response.data.data;
};

/** "Join 1:1 instead" nudge for a user sitting alone in a circle (triage #4). */
export interface OneOnOneNudge {
  should_nudge: boolean;
  join_as: 'giver' | 'seeker' | null;
  waiting_count: number;
}

export const getOneOnOneNudge = async (): Promise<OneOnOneNudge> => {
  const response = await api.get('/empathy-queue/one-on-one-nudge');
  return response.data;
};

export interface ActiveCohortSession {
  circle_id: number;
  cohort_id: number;
  cohort_name: string;
  cohort_color: string | null;
  status: string;
  started_at: string | null;
  /**
   * The viewer joined this circle and then left. The row is still returned —
   * leaving is not the same as having no way back — and each consumer decides
   * what to do with it. The banner skips these (it is a notification, and
   * re-announcing a session you walked out of is nagging); "Your Groups" keeps
   * a persistent Enter button, which is the durable route back in.
   */
  viewer_has_left: boolean;
}

export interface ScheduleSession {
  session_id: number;
  cohort_id: number;
  cohort_name: string;
  color: string | null;
  scheduled_at: string;
  duration_minutes: number;
}

export const getMySchedule = async (days: number = 7): Promise<ScheduleSession[]> => {
  const response = await api.get('/circles/my-schedule', { params: { days } });
  return response.data.data;
};

export const getActiveCohortSessions = async (): Promise<ActiveCohortSession[]> => {
  return (await getActiveCohortFeed()).sessions;
};

/**
 * The app-wide poll, whole: the live cohort sessions plus the listener's
 * side (P7d): the in-app ring, today's "You're available" notice, and
 * whether the ladder would ring them now. Older servers send only `data`.
 */
export interface ActiveCohortFeed {
  sessions: ActiveCohortSession[];
  ring: PendingRing | null;
  notice: AvailabilityNotice | null;
  availableNow: boolean;
  /** R11: a call waiting for this person as the seeker, or null. */
  incomingCall: IncomingCall | null;
  /** R14: a caller who gave up on this person's request, once, or null. */
  missedCall?: MissedCall | null;
  /** The circle-starting-soon notice, or null (CIRCLE-STARTING-NOTICE §4). */
  startingSoon?: StartingSoon | null;
}

/** The zone the browser reports, for the listener's hours on the sheet (as the profile sends it). */
function clientTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export const getActiveCohortFeed = async (): Promise<ActiveCohortFeed> => {
  const tz = clientTimeZone();
  const response = tz
    ? await api.get('/circles/active-cohort-sessions', { params: { tz } })
    : await api.get('/circles/active-cohort-sessions');
  const body = response.data ?? {};
  return {
    sessions: body.data ?? [],
    ring: body.ring ?? null,
    notice: body.availability_notice ?? null,
    availableNow: body.available_now === true,
    incomingCall: body.incoming_call ?? null,
    missedCall: body.missed_call ?? null,
    startingSoon: body.starting_soon ?? null,
  };
};

/**
 * `expectedScheduledAt`: the hour the caller chose (the starting-soon notice,
 * the pending join). Landing after it is a 409 `missed`, never a signup for
 * the following hour. Without it, the hub's "the next one" behaviour.
 */
export const signUpForCircle = async (
  expectedScheduledAt?: string,
): Promise<{
  circle_id: number;
  scheduled_at: string;
  signup_count: number;
}> => {
  const response = expectedScheduledAt
    ? await api.post('/circles/signup', { expected_scheduled_at: expectedScheduledAt })
    : await api.post('/circles/signup');
  return response.data.data;
};

/** What happened to the starting-soon notice (shown once; the rest move it forward). */
export const recordStartingNotice = async (
  circleId: number,
  event: StartingNoticeEvent,
): Promise<void> => {
  const platform = Capacitor.getPlatform();
  await api.post(`/circles/${circleId}/starting-notice`, {
    event,
    platform: platform === 'ios' || platform === 'android' ? platform : 'web',
  });
};

/**
 * The circle a signed-out visitor is invited into (CIRCLE-STARTING-NOTICE.md
 * §5): public, cached, allow-listed. Polled only by PublicStartingSoonHost.
 */
export const getPublicStartingSoon = async (): Promise<PublicStartingSoonResponse> => {
  const response = await api.get('/circles/starting-soon');
  return response.data as PublicStartingSoonResponse;
};

export const cancelCircleSignup = async (): Promise<void> => {
  await api.delete('/circles/signup');
};

export const joinCircle = async (circleId: number): Promise<CircleJoinData> => {
  const response = await api.post(`/circles/${circleId}/join`);
  return response.data.data;
};

/**
 * Dev-only: ensure + join the shared Circle Test Room (open-discussion
 * Janus room for verifying audio / reaction parity / speaker halo).
 * Returns the underlying circle id; the caller then runs the real
 * joinCircle()/poll path against it. 404s in production (route not registered).
 */
export const joinCircleTestRoom = async (): Promise<{ circle_id: number }> => {
  const response = await api.post('/dev/circle-test-room/join');
  return { circle_id: response.data.circle_id };
};

/** What the in-room rehearsal controls can ask the engine to do. */
export type RehearsalAction = 'next-turn' | 'swap' | 'discussion' | 'exchange' | 'end';

/**
 * Dev-only: start (or rejoin) this user's EXCHANGE REHEARSAL.
 *
 * The test room above pins an open discussion forever, which is the one phase
 * in which a camera control cannot exist — cameras are an exchange feature. So
 * this is its opposite: two seats, thirty-second turns, and it ends itself.
 * Idempotent, so a reload rejoins rather than minting a second circle.
 * 404s in production (route not registered).
 */
export const startCircleRehearsal = async (): Promise<{
  circle_id: number;
  turn_seconds: number;
  expires_at: string | null;
}> => {
  const response = await api.post('/dev/circle-rehearsal/start');
  return {
    circle_id: response.data.circle_id,
    turn_seconds: response.data.turn_seconds,
    expires_at: response.data.expires_at ?? null,
  };
};

/**
 * Dev-only: one rehearsal control press.
 *
 * Resolves to the phase the engine landed in, or REJECTS with the server's own
 * sentence — a refusal ("this rehearsal timed out", "that is not yours") has to
 * reach the button, or a control that did nothing looks exactly like one that
 * worked.
 */
export const circleRehearsalAction = async (
  circleId: number,
  action: RehearsalAction,
): Promise<{ phase: string | null }> => {
  const response = await api.post(`/dev/circle-rehearsal/${circleId}/${action}`);
  return { phase: response.data.phase ?? null };
};

export const getCircleState = async (circleId: number): Promise<CircleRoomState | null> => {
  const response = await api.get(`/circles/${circleId}/state`);
  return response.data.data;
};

/**
 * `fromPhase` is the phase this advance means to end, decided on the phase the
 * client last read. The server refuses it with 409 `phase_moved` when the room
 * has already left that phase, so a late "the meditation is over" cannot end
 * the exchange turn a poll has just started.
 */
export const advanceCircleTurn = async (
  circleId: number,
  fromPhase: CirclePhase,
): Promise<CircleRoomState | null> => {
  const response = await api.post(`/circles/${circleId}/turn/advance`, { phase: fromPhase });
  return response.data.data;
};

export const endCircleEarly = async (circleId: number): Promise<CircleRoomState | null> => {
  const response = await api.post(`/circles/${circleId}/turn/end-early`);
  return response.data.data;
};

export const setCircleListeningRequest = async (
  circleId: number,
  request: ListeningRequest,
): Promise<void> => {
  await api.post(`/circles/${circleId}/turn/listening-request`, { request });
};

export interface MuteResult {
  muted: boolean;
  muted_users: number[];
  /**
   * Did the change actually reach the audio bridge?
   *
   * `false` means the server RECORDED the mute but Janus refused to apply it,
   * so the mic is still live right now. `null` means no bridge was involved.
   *
   * This return value used to be `void`. The server reported the refusal and
   * the client threw the whole response away, so somebody could tap mute, see
   * it succeed, and still be broadcasting. (Codex P1 on PR #800.)
   */
  bridge_synced: boolean | null;
}

export const setCircleUserMute = async (
  circleId: number,
  muted: boolean,
): Promise<MuteResult | null> => {
  const response = await api.post(`/circles/${circleId}/turn/mute`, { muted });

  return response.data?.data ?? null;
};

/**
 * `config` carries the ending session's `sessionToken` (api.ts) when an
 * account change takes the account out of its live circle: by then the auth
 * store may already hold the next account's token.
 */
export const leaveCircle = async (circleId: number, config?: AxiosRequestConfig): Promise<void> => {
  const url = `/circles/${circleId}/leave`;
  await (config ? api.post(url, undefined, config) : api.post(url));
};

/**
 * Leaving, on the way out of the door.
 *
 * The unmount path awaits a plain axios POST, which the browser is free to
 * abandon the moment the page goes — so closing a tab, swiping the app away or
 * following a link out of the session told the server nothing at all, and the
 * row said "present" until the server's own silence sweep noticed ninety
 * seconds later. `keepalive` is what makes the request outlive the page; an
 * Authorization header is what sendBeacon cannot carry, which is why this is a
 * fetch and not a beacon (same shape, and the same reason, as
 * flushCircleReactionBatchKeepalive above).
 *
 * Best-effort by design: there is nothing to report to, and the server-side
 * sweep is the backstop for everything this cannot reach (an OS-killed app
 * never runs any of it).
 */
export const leaveCircleKeepalive = (circleId: number): void => {
  try {
    const token = useAuthStore.getState().token;
    void fetch(`${import.meta.env.VITE_API_URL}/circles/${circleId}/leave`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: '{}',
    }).catch(() => {});
  } catch {
    /* unload best-effort */
  }
};

export const getCirclePostSession = async (circleId: number): Promise<CirclePostSession> => {
  const response = await api.get(`/circles/${circleId}/post-session`);
  return response.data.data;
};

export const getCircleHistory = async (): Promise<{ data: CircleHistoryEntry[] }> => {
  const response = await api.get('/circles/history');
  return response.data.data;
};

export const submitMoodCheckIn = async (
  circleId: number,
  emotion: EkmanEmotion,
): Promise<MoodCheckInEntry> => {
  const response = await api.post(`/circles/${circleId}/mood/check-in`, { emotion });
  return response.data.data;
};

export const skipMoodCheckIn = async (circleId: number): Promise<void> => {
  await api.post(`/circles/${circleId}/mood/skip`);
};

export const submitMoodCheckOut = async (
  circleId: number,
  emotion: EkmanEmotion,
): Promise<MoodCheckInEntry> => {
  const response = await api.post(`/circles/${circleId}/mood/check-out`, { emotion });
  return response.data.data;
};

export const getMoodHistory = async (): Promise<MoodCheckInEntry[]> => {
  const response = await api.get('/circles/mood/history');
  return response.data.data;
};

export const sendCircleReaction = async (
  circleId: number,
  targetSpeakerId: number,
  reactionType: ReactionType,
  roomNumber: number,
): Promise<void> => {
  await api.post(`/circles/${circleId}/reactions`, {
    target_speaker_id: targetSpeakerId,
    reaction_type: reactionType,
    room_number: roomNumber,
  });
};

export interface ReactionBatchPayload {
  batch_id: string;
  room_number: number;
  reactions: Record<string, number>;
  span_ms: number;
  target_speaker_id?: number;
  /** Sender's active super-reaction variant so a SUPER looks the same on
   *  every screen (the variant is otherwise a per-browser dev setting). */
  variant?: string;
}

/** Client-batched reactions — one packet of {type:count} instead of N posts. */
export const sendCircleReactionBatch = async (
  circleId: number,
  payload: ReactionBatchPayload,
  config?: AxiosRequestConfig,
): Promise<void> => {
  const url = `/circles/${circleId}/reactions/batch`;
  await (config ? api.post(url, payload, config) : api.post(url, payload));
};

/**
 * Teardown flush. sendBeacon can't set the Authorization header; fetch
 * with keepalive can and still completes during page unload. Best-effort:
 * a lost final batch of ephemeral reactions is acceptable.
 */
export const flushCircleReactionBatchKeepalive = (
  circleId: number,
  payload: ReactionBatchPayload,
): void => {
  try {
    const token = useAuthStore.getState().token;
    void fetch(`${import.meta.env.VITE_API_URL}/circles/${circleId}/reactions/batch`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch {
    /* unload best-effort */
  }
};

export const getReactionSummary = async (circleId: number): Promise<ReactionSummaryEntry[]> => {
  const response = await api.get(`/circles/${circleId}/reactions/summary`);
  return response.data.data;
};

export const reportParticipant = async (
  circleId: number,
  reportedUserId: number,
  reason: string,
): Promise<void> => {
  await api.post(`/circles/${circleId}/report`, {
    reported_user_id: reportedUserId,
    reason,
  });
};

/** The five report categories (Apple App Review Guideline 1.2). */
export type ReportCategory =
  | 'urgent_support'
  | 'confidentiality'
  | 'harassment'
  | 'inappropriate'
  | 'spam'
  | 'other';

export type ReportContext = 'circle' | 'one_on_one' | 'profile';

export interface ReportUserParams {
  reportedUserId: number;
  category: ReportCategory;
  reason?: string;
  /** Present for circle/1:1 reports; routes to the circle endpoint. */
  circleId?: number;
  context: ReportContext;
}

/**
 * Report a user from any surface. A circle-scoped report (context==='circle'
 * with a circleId) goes to the circle endpoint so it's tied to that session;
 * everything else (1:1, profile) goes to the standalone user-report endpoint.
 */
export const reportUser = async ({
  reportedUserId,
  category,
  reason,
  circleId,
  context,
}: ReportUserParams): Promise<void> => {
  if (circleId && context === 'circle') {
    await api.post(`/circles/${circleId}/report`, {
      reported_user_id: reportedUserId,
      // The circle endpoint requires a non-empty reason; fall back to the
      // category label so a category-only report still validates.
      reason: reason && reason.trim() ? reason : category,
      category,
    });
    return;
  }

  await api.post(`/users/${reportedUserId}/report`, {
    category,
    reason,
    context,
  });
};

export const saveCircleJournal = async (
  circleId: number,
  entry: string,
): Promise<CircleJournal> => {
  const response = await api.post(`/circles/${circleId}/journal`, { entry });
  return response.data.data;
};

export const getCircleStats = async (): Promise<{
  sessions_attended: number;
  reflections_count: number;
}> => {
  const response = await api.get('/circles/stats');
  return response.data.data;
};

export const getMyJournals = async (
  page = 1,
): Promise<{ data: CircleJournal[]; current_page: number; last_page: number; total: number }> => {
  const response = await api.get('/me/journals', { params: { page } });
  return response.data.data;
};

export const getPublicCohort = async (id: number): Promise<PublicCohortData> => {
  const response = await api.get(`/circles/public/${id}`);
  return response.data.data;
};

/**
 * Privacy-safe "not-live" summary of a circle (no join). Powers the
 * session-status screen shown when a circle isn't live. A 404 here is the
 * intended fail-closed signal (private-non-member or truly-not-found — the
 * server makes them identical); callers render a generic "unavailable".
 */
export const getCircleSummary = async (id: number): Promise<CircleSummary> => {
  const response = await api.get(`/circles/${id}/summary`);
  return response.data.data;
};

export const warmupPass = async (
  circleId: number,
  nextUserId: number,
): Promise<CircleRoomState | null> => {
  const response = await api.post(`/circles/${circleId}/turn/warmup-pass`, {
    next_user_id: nextUserId,
  });
  return response.data.data ?? null;
};

/**
 * "I still can't hear anyone" — raised from the audio recovery panel.
 *
 * Only reachable after the client has confirmed it can still reach us, so a
 * row written here means the product failed this person rather than that their
 * connection dropped. The client diagnostic rides on the same row: asking
 * somebody to describe a WebRTC failure in prose gets "it didn't work".
 */
export const reportAudioProblem = async (
  circleId: number,
  payload: {
    level: string;
    reachability: string;
    down_for_ms: number | null;
    diagnostic: unknown[];
  },
): Promise<void> => {
  await api.post(`/circles/${circleId}/audio-report`, payload);
};

/**
 * "End session for everyone" — the organizer's close.
 *
 * GRACEFUL on the server: every room that can jumps to its wind-down, so the
 * melody and the check-out still happen and the circle reaches its ordinary
 * ending. Only a session already stuck at that point gets the hard stop. The
 * response says which happened via `wound_down`, which is what lets the UI
 * tell the truth about what it just did.
 */
export const endCircleSession = async (
  circleId: number,
): Promise<{ woundDown: boolean; message: string }> => {
  const response = await api.post(`/circles/${circleId}/end-session`);

  return {
    woundDown: response.data?.wound_down === true,
    message: String(response.data?.message ?? ''),
  };
};
